import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isHttpUrl } from '@bekebi/domain';
import { Button } from '../../components/button';
import { useUpdateWebsiteAddress } from './api';

/**
 * Website public address (PATCH /api/services/:id). Accepts an absolute http(s) address — the
 * API's own rule — and additionally refuses embedded credentials, which have no place in a public
 * address. Leaving it empty removes the address. The API remains the authority; after a change the
 * service is re-read, so the health shown is whatever the API reports (the Website check restarts).
 */
const addressSchema = z.object({
  accessUrl: z
    .string()
    .trim()
    .max(2048, 'That address is too long.')
    .refine(
      (v) => v === '' || isHttpUrl(v),
      'Enter a full address starting with https:// or http://.',
    )
    .refine((v) => {
      if (v === '') return true;
      try {
        const url = new URL(v);
        return !url.username && !url.password;
      } catch {
        return false;
      }
    }, 'The address must not contain a user name or password.'),
});
type AddressForm = z.infer<typeof addressSchema>;

export function WebsiteAddressForm({
  serviceId,
  current,
}: {
  serviceId: string;
  current: string | null;
}) {
  const update = useUpdateWebsiteAddress(serviceId);
  const [saved, setSaved] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddressForm>({
    resolver: zodResolver(addressSchema),
    defaultValues: { accessUrl: current ?? '' },
  });

  const onSubmit = handleSubmit((values) => {
    setSaved(null);
    const next = values.accessUrl === '' ? null : values.accessUrl;
    update.mutate(next, {
      onSuccess: () =>
        setSaved(
          next === null
            ? 'The address was removed.'
            : "The address was saved. The Website check's last result was cleared; a new result appears when the monitoring worker next checks the new address.",
        ),
    });
  });

  return (
    <form onSubmit={onSubmit} noValidate aria-label="Change public address" className="space-y-3">
      <div>
        <label htmlFor="website-address" className="block text-sm font-medium text-fg-2">
          New public address
        </label>
        <input
          id="website-address"
          type="url"
          inputMode="url"
          autoComplete="url"
          placeholder="https://"
          aria-invalid={errors.accessUrl ? 'true' : 'false'}
          aria-describedby="website-address-hint"
          className="mt-1 block w-full max-w-xl rounded-md border border-line-strong bg-surface px-3 py-2 font-mono text-sm"
          {...register('accessUrl')}
        />
        <p id="website-address-hint" className="mt-1 text-xs text-muted">
          Leave empty to remove the address.
        </p>
        {errors.accessUrl ? (
          <p className="mt-1 text-sm text-danger">{errors.accessUrl.message}</p>
        ) : null}
      </div>
      {update.isError ? (
        <p role="alert" className="text-sm text-danger">
          {update.error.message}
        </p>
      ) : null}
      {saved ? (
        <p role="status" className="text-sm text-success">
          {saved}
        </p>
      ) : null}
      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? 'Saving…' : 'Save address'}
      </Button>
    </form>
  );
}
